import { Box, Typography, Switch, Tooltip, Chip } from "@mui/material";
import { useState } from "react";
import { apiConstants, postGeneric } from '../../common/constants'
import * as React from 'react'
import Confirm from './confirm';
import { createNotificationConfig } from "../../helpers/UtilityHelper";
import { useSnackbar } from "notistack";
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER } from '../../styles/foundation/colors';

const CustomerNotification = ({ merchantId, setReload, data }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [selectedLabel, setSelectedLabel] = useState('');
    const [channel, setChannel] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const [isActive, setIsActive] = useState({
        endCustomerWA_B2B: false,
        endCustomerSMS_B2B: false,
        endCustomerWA_B2C: false,
        endCustomerSMS_B2C: false,
        endCustomerWA_RVP: false,
        endCustomerSMS_RVP: false,
        endCustomerEMAIL_RVP: false,
    });

    const notificationConfig = createNotificationConfig(data);

    const endCustomerWA_B2B = notificationConfig["end_customer_Notification_WA_B2B"];
    const endCustomerSMS_B2B = notificationConfig["end_customer_Notification_SMS_B2B"];
    const endCustomerSMS_B2C = notificationConfig["end_customer_Notification_SMS_B2C"];
    const endCustomerWA_B2C = notificationConfig["end_customer_Notification_WA_B2C"];
    // B2C reverse (RVP) shipments: separate subscription (alertSubscriptionRvp), same toggle UX as forward
    const endCustomerWA_RVP = notificationConfig["end_customer_Notification_WA_RVP"];
    const endCustomerSMS_RVP = notificationConfig["end_customer_Notification_SMS_RVP"];
    const endCustomerEMAIL_RVP = notificationConfig["end_customer_Notification_EMAIL_RVP"];

    // Events written per section when a channel is switched on. B2C RVP is pickup-only by design:
    // after pickup the parcel is heading to the merchant's warehouse, which is not the customer's concern.
    const FORWARD_EVENTS = ["PLACED", "PICKED", "LIVE", "DELAY", "OUT_FOR_DELIVERY", "DELIVERED", "NDR"];
    const RVP_EVENTS = ["RVP_PICKED"];

    const showPrice = (name) => {
        if (!name?.isActive) return null;
        const priceValue = (name?.configValue !== '0.0' && name?.configValue !== '0' && name?.configValue !== null)
            ? name?.configValue
            : name?.defaultPriceValue;
        return (
            <Box sx={{ ml: 0, mt: 0.5 }}>
                <Typography component="span" sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>Price: </Typography>
                <Typography component="span" sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>₹{priceValue}</Typography>
            </Box>
        );
    };

    // to store status
    React.useEffect(() => {
        setIsActive({
            endCustomerWA_B2B: endCustomerWA_B2B?.isActive,
            endCustomerSMS_B2B: endCustomerSMS_B2B?.isActive,
            endCustomerWA_B2C: endCustomerWA_B2C?.isActive,
            endCustomerSMS_B2C: endCustomerSMS_B2C?.isActive,
            endCustomerWA_RVP: endCustomerWA_RVP?.isActive,
            endCustomerSMS_RVP: endCustomerSMS_RVP?.isActive,
            endCustomerEMAIL_RVP: endCustomerEMAIL_RVP?.isActive,
        })
    }, [data])

    const handleToggle = (config, channel, label) => {
        // The section/channel are derived from config.pricingType. If the backend has not sent that
        // row yet (e.g. mid-rollout), bail out — otherwise the derivation falls back to B2C/SMS and
        // the toggle would write to the wrong subscription.
        if (!config?.pricingType) {
            enqueueSnackbar("This notification option is not available yet", { variant: "warning" });
            return;
        }
        setSelectedConfig(config);
        setChannel(channel);
        setSelectedLabel(label);
        setOpenDialog(true);
    };

    const handleConfirm = () => {
        toggleStatus(selectedConfig);
        setOpenDialog(false);
    };

    const hasConfigValue = () => {
        return selectedConfig?.configValue !== '0.0' &&
            selectedConfig?.configValue !== '0' &&
            selectedConfig?.configValue !== null;
    };

    const getDefaultPrice = () => {
        return selectedConfig?.defaultPriceValue || '0';
    };

    // Which subscription bucket a pricing row belongs to: B2B, RVP (B2C reverse) or B2C forward.
    const getSection = (pricingType) => {
        if (pricingType?.includes('B2B')) return 'B2B';
        if (pricingType?.includes('RVP')) return 'RVP';
        return 'B2C';
    };

    // Which channel a pricing row drives. EMAIL must be tested before WA, since neither
    // substring appears in the other but the fallback would otherwise swallow it.
    const getChannel = (pricingType) => {
        if (pricingType?.includes('EMAIL')) return 'EMAIL';
        if (pricingType?.includes('WA')) return 'WHATSAPP';
        return 'SMS';
    };

    // Channels each card owns. The backend merges per channel, so a channel this card does not
    // send is left untouched — that is what stops a forward WhatsApp/SMS toggle from clearing a
    // merchant's EMAIL subscription.
    const SECTION_CHANNELS = {
        B2B: ['WHATSAPP', 'SMS'],
        B2C: ['WHATSAPP', 'SMS'],
        RVP: ['WHATSAPP', 'SMS', 'EMAIL'],
    };

    const STATE_KEY_PREFIX = {
        WHATSAPP: 'endCustomerWA_',
        SMS: 'endCustomerSMS_',
        EMAIL: 'endCustomerEMAIL_',
    };

    const toggleStatus = (config) => {
        const section = getSection(config?.pricingType);
        const toggledChannel = getChannel(config?.pricingType);
        const events = section === 'RVP' ? RVP_EVENTS : FORWARD_EVENTS;

        // Send explicit state for every channel on this card: the event list switches a channel on,
        // an empty list switches it off.
        const subscriptions = {};
        (SECTION_CHANNELS[section] || []).forEach((channel) => {
            const currentlyOn = !!isActive[`${STATE_KEY_PREFIX[channel]}${section}`];
            const willBeOn = channel === toggledChannel ? !currentlyOn : currentlyOn;
            subscriptions[channel] = willBeOn ? events : [];
        });

        const payload = { subscriptions };

        const endpointBySection = {
            B2B: apiConstants.POST_SUBSCRIPTION_B2B,
            RVP: apiConstants.POST_SUBSCRIPTION_RVP,
            B2C: apiConstants.POST_SUBSCRIPTION,
        };
        const endpoint = `${endpointBySection[section]}/${merchantId}`;

        postGeneric(endpoint, payload)
            .then(response => {
                if (response.status === 200 && response?.data?.status === "SUCCESS") {
                    enqueueSnackbar("Updated successfully", { variant: "success" });
                    setReload(prev => prev + 1);
                } else {
                    enqueueSnackbar(response?.data?.responseObj, { variant: "error" });
                }
            })
            .catch(err => {
                enqueueSnackbar(err, { variant: "error" });
            });
    };

    const Toggle = ({ type, label, isActive, onToggle }) => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            borderTop: `1px solid ${BORDER}`,
        }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: NAVY }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                    label={isActive ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                        height: 22,
                        fontSize: '11px',
                        fontFamily: FONT,
                        fontWeight: WEIGHT.semibold,
                        bgcolor: isActive ? '#e8f5e9' : '#fce4ec',
                        color: isActive ? '#2e7d32' : '#c62828',
                        border: 'none',
                    }}
                />
                <Tooltip title={isActive ? "Disable" : "Enable"}>
                    <Switch
                        checked={isActive}
                        onChange={() => onToggle(type, isActive)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                    />
                </Tooltip>
            </Box>
        </Box>
    );

    return (
        <>
            <Confirm
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                isActive={selectedConfig?.isActive}
                title={selectedLabel}
                hasConfigValue={() => hasConfigValue()}
                getDefaultPrice={() => getDefaultPrice()}
                handleConfirm={handleConfirm}
                configType={channel}
                selectedConfig={selectedConfig}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* B2B Section */}
                <Box sx={{
                    flex: 1,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    p: 2.5,
                }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY, mb: 0.5 }}>
                        B2B Notifications
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 1 }}>
                        Configure end customer notifications for PTL Shipments
                    </Typography>
                    <Toggle type="endCustomerWA_B2B" label="WhatsApp Notifications" isActive={isActive.endCustomerWA_B2B} onToggle={() => handleToggle(endCustomerWA_B2B, 'WA', 'WhatsApp notifications (B2B)')} />
                    {showPrice(endCustomerWA_B2B)}
                    <Toggle type="endCustomerSMS_B2B" label="SMS Notifications" isActive={isActive.endCustomerSMS_B2B} onToggle={() => handleToggle(endCustomerSMS_B2B, 'SMS', 'SMS notifications (B2B)')} />
                    {showPrice(endCustomerSMS_B2B)}
                </Box>

                {/* B2C Forward Section */}
                <Box sx={{
                    flex: 1,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    p: 2.5,
                }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY, mb: 0.5 }}>
                        B2C Forward Notifications
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 1 }}>
                        Configure end customer notifications for forward D2C Shipments
                    </Typography>
                    <Toggle type="endCustomerWA_B2C" label="WhatsApp Notifications" isActive={isActive.endCustomerWA_B2C} onToggle={() => handleToggle(endCustomerWA_B2C, 'WA', 'WhatsApp notifications (B2C)')} />
                    {showPrice(endCustomerWA_B2C)}
                    <Toggle type="endCustomerSMS_B2C" label="SMS Notifications" isActive={isActive.endCustomerSMS_B2C} onToggle={() => handleToggle(endCustomerSMS_B2C, 'SMS', 'SMS notifications (B2C)')} />
                    {showPrice(endCustomerSMS_B2C)}
                </Box>

                {/* B2C RVP Section */}
                <Box sx={{
                    flex: 1,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    p: 2.5,
                }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY, mb: 0.5 }}>
                        B2C RVP Notifications
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 1 }}>
                        Configure end customer notifications for reverse pickup (RVP) Shipments
                    </Typography>
                    <Toggle type="endCustomerWA_RVP" label="WhatsApp Notifications" isActive={isActive.endCustomerWA_RVP} onToggle={() => handleToggle(endCustomerWA_RVP, 'WA', 'WhatsApp notifications (B2C RVP)')} />
                    {showPrice(endCustomerWA_RVP)}
                    <Toggle type="endCustomerSMS_RVP" label="SMS Notifications" isActive={isActive.endCustomerSMS_RVP} onToggle={() => handleToggle(endCustomerSMS_RVP, 'SMS', 'SMS notifications (B2C RVP)')} />
                    {showPrice(endCustomerSMS_RVP)}
                    {/* Email has no per-message charge, so no price line is shown for it. */}
                    <Toggle type="endCustomerEMAIL_RVP" label="Email Notifications" isActive={isActive.endCustomerEMAIL_RVP} onToggle={() => handleToggle(endCustomerEMAIL_RVP, 'EMAIL', 'Email notifications (B2C RVP)')} />
                </Box>
            </Box>
        </>
    )
}

export default CustomerNotification;
